import { z } from 'zod';

/** A label that fails to parse is dropped on its own, so a bad label costs the label and never the card. */
function optionalLabel<Schema extends z.ZodTypeAny>(schema: Schema) {
  return schema.optional().catch(undefined);
}

const counterpartSchema = z.object({
  id: z.number().int(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  identifiesAsDoctor: z.boolean().optional(),
  userType: z.string().optional(),
});

const cancerTypeSchema = z.object({
  id: z.number().int(),
  label: z.string().min(1),
  shortLabel: z.string().nullable().optional(),
});

const referencedInquirySchema = z.object({
  id: z.number().int().positive(),
  conversationId: z.string().min(1),
  question: optionalLabel(z.string().nullable()),
  createdAt: optionalLabel(z.string().nullable()),
  state: optionalLabel(z.enum(['active', 'closed']).nullable()),
  role: optionalLabel(z.enum(['submitter', 'recipient'])),
  counterpart: optionalLabel(counterpartSchema.nullable()),
  cancerType: optionalLabel(cancerTypeSchema.nullable()),
});

export type ReferencedInquiry = z.infer<typeof referencedInquirySchema>;

/**
 * An inquiry as the support thread shows it wherever staff pick or a message
 * carries one: the attach list's entries and the details the API snapshots onto
 * a message when it is sent with a reference. Both arrive in this shape, so the
 * picker row and the bubble card read the same.
 *
 * It is identified by the other participant and the opening question, not the
 * inquiry title: cancer focus, age and diagnosis are often left blank, so every
 * row of a member's inquiries would read alike.
 *
 * Null when the ids are missing or malformed, which decides whether there is a
 * card at all. Every label is optional and dropped alone when it does not
 * parse: messages stored before the snapshot existed carry none, and a partial
 * snapshot must cost only that label, never the thread.
 *
 * Kept apart from the thread service, which pulls in the API client, so the
 * pure thread-item builder can use it without loading auth.
 */
export function toReferencedInquiry(value: unknown): ReferencedInquiry | null {
  const result = referencedInquirySchema.safeParse(value);

  return result.success ? result.data : null;
}
